import { useEffect, useRef, useState, ReactNode } from "react";
import styles from "./styles/HorizontalScrollContainer.module.scss";
import FontAwesome from "../utilities/FontAwesome";
import classNames from "classnames";
import { IconButton } from "@radix-ui/themes";

interface HorizontalScrollContainerProps {
  children: ReactNode;
  /** Additional class applied to the outer wrapper div */
  className?: string;
  /** Additional class applied to the scrollable inner div */
  scrollContainerClassName?: string;
  scrollItemClassName: string;
}

const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  children,
  className,
  scrollContainerClassName,
  scrollItemClassName,
}) => {
  const [hideScrollLeft, setHideScrollLeft] = useState(true);
  const [hideScrollRight, setHideScrollRight] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  const isScrolledToEnd = (el: HTMLElement) =>
    Math.abs(el.scrollWidth - el.clientWidth - el.scrollLeft) < 1;

  const getItems = () =>
    Array.from(
      scrollContainerRef.current?.querySelectorAll(`.${scrollItemClassName}`) ??
        [],
    ) as HTMLElement[];

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const containerLeft = container.getBoundingClientRect().left;
    const items = getItems();
    const target =
      direction === "right"
        ? items.find(
            (el) => el.getBoundingClientRect().left > containerLeft + 1,
          )
        : items
            .reverse()
            .find((el) => el.getBoundingClientRect().left < containerLeft - 1);
    if (target) {
      container.scrollTo({
        left:
          container.scrollLeft +
          (target.getBoundingClientRect().left - containerLeft),
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollButtons = () => {
      setHideScrollLeft((container.scrollLeft ?? 0) < 5);
      setHideScrollRight(isScrolledToEnd(container));

      if (!isMounted) {
        // this prevents the component from displaying prior to determining if scroll buttons should appear
        setIsMounted(true);
      }
    };

    container.addEventListener("scroll", updateScrollButtons);
    updateScrollButtons();

    return () => container.removeEventListener("scroll", updateScrollButtons);
  }, []);

  const wrapperClassName = classNames({
    [styles.wrapper]: true,
    [className || ""]: !!className,
    [styles.wrapperHidden]: !isMounted,
  });

  return (
    <div className={wrapperClassName}>
      <IconButton
        radius={"full"}
        aria-label="Previous Section"
        className={classNames(styles.scrollLeft, {
          [styles.hidden]: hideScrollLeft,
        })}
        onClick={() => scroll("left")}
      >
        <FontAwesome
          name="fa-chevron-left"
          prefix="fas"
          fixedWidth
          title="Previous"
        />
      </IconButton>
      <div
        className={classNames(styles.scrollContainer, scrollContainerClassName)}
        ref={scrollContainerRef}
      >
        {children}
      </div>
      <IconButton
        radius={"full"}
        aria-label="Next Section"
        className={classNames(styles.scrollRight, {
          [styles.hidden]: hideScrollRight,
        })}
        onClick={() => scroll("right")}
      >
        <FontAwesome
          name="fa-chevron-right"
          prefix="fas"
          fixedWidth
          title="Next"
        />
      </IconButton>
      <div
        className={classNames(styles.overlayRight, {
          [styles.hidden]: hideScrollRight,
          [styles.overlayRightWithArrows]: !hideScrollRight,
        })}
      />
      <div
        className={classNames(styles.overlayLeft, {
          [styles.hidden]: hideScrollLeft,
          [styles.overlayLeftWithArrows]: !hideScrollLeft,
        })}
      />
    </div>
  );
};

export default HorizontalScrollContainer;
